import { processListingParams } from '@/api/helpers';
import {
  getExceptionFromAxiosError,
  USER_ALREADY_EXISTS_ERROR,
} from '@/client/helpers';
import { MessageResponseModel } from '@/client/models';
import {
  ExportAccountUsersRequest,
  ListUserCampaignsRequest,
  ListUserCampaignsResponse,
  ListUserCoursesRequest,
  ListUserCoursesResponse,
  ListUsersRequest,
  ListUsersResponse,
  SaveUserRequest,
  UpdateUserRequest,
  User,
  UsernameLookupResult,
} from '@/client/users';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { omit } from 'lodash';

export const usersService = (client: AxiosInstance) => {
  const USERS_BASE_URL = '/users';
  const ACCOUNT_USERS_BASE_URL = (accountId?: string) =>
    `accounts/${accountId}/users`;

  const getUsers = async (
    params: ListUsersRequest & { withBranches?: boolean },
  ): Promise<ListUsersResponse> => {
    try {
      const result = await client.get<
        ListUsersResponse,
        AxiosResponse<ListUsersResponse>
      >(USERS_BASE_URL, {
        withCredentials: true,
        params: {
          ...processListingParams(omit(params, 'withBranches')),
          withBranches: params.withBranches ? 1 : 0,
        },
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getAccountUsers = async (
    params: ListUsersRequest & { withBranches?: boolean },
    accountId?: string,
  ): Promise<ListUsersResponse> => {
    try {
      const result = await client.get<
        ListUsersResponse,
        AxiosResponse<ListUsersResponse>
      >(ACCOUNT_USERS_BASE_URL(accountId), {
        withCredentials: true,
        params: {
          ...processListingParams(omit(params, 'withBranches')),
          withBranches: params.withBranches ? 1 : 0,
        },
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getUser = async (
    userId?: string,
    accountId?: string,
  ): Promise<User> => {
    try {
      const result = await client.get<User, AxiosResponse<User>>(
        `${ACCOUNT_USERS_BASE_URL(accountId)}/${userId}`,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const saveUser = async (request: SaveUserRequest): Promise<User> => {
    try {
      const result = await client.post<User, AxiosResponse<User>>(
        ACCOUNT_USERS_BASE_URL(request.accountId),
        request,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e: any) {
      // Temporary solution
      if (e.message === USER_ALREADY_EXISTS_ERROR) {
        throw new Error(USER_ALREADY_EXISTS_ERROR);
      }

      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateUser = async (request: UpdateUserRequest): Promise<User> => {
    try {
      const result = await client.patch<User, AxiosResponse<User>>(
        `${ACCOUNT_USERS_BASE_URL(request.accountId)}/${request.userId}`,
        request.updates,
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const deleteUser = async (
    userId: string,
    accountId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.delete<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${ACCOUNT_USERS_BASE_URL(accountId)}/${userId}`, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const deleteUsers = async (
    userIds: string[],
    accountId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.delete<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(ACCOUNT_USERS_BASE_URL(accountId), {
        withCredentials: true,
        data: { userIds },
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const exportUsers = async (
    request: ExportAccountUsersRequest,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${ACCOUNT_USERS_BASE_URL(request.accountId)}/export`,
        { ...request },
        {
          withCredentials: true,
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getUserCourses = async (
    params: ListUserCoursesRequest,
    accountId?: string,
    userId?: string,
  ): Promise<ListUserCoursesResponse> => {
    try {
      const result = await client.get<
        ListUserCoursesResponse,
        AxiosResponse<ListUserCoursesResponse>
      >(`${ACCOUNT_USERS_BASE_URL(accountId)}/${userId}/courses`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getUserCampaigns = async (
    params: ListUserCampaignsRequest,
    userId?: string,
  ): Promise<ListUserCampaignsResponse> => {
    try {
      const result = await client.get<
        ListUserCampaignsResponse,
        AxiosResponse<ListUserCampaignsResponse>
      >(`${USERS_BASE_URL}/${userId}/campaigns`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const deleteAnonymizedUsers = async (
    accountId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.delete<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${ACCOUNT_USERS_BASE_URL(accountId)}/anonymized`, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const checkUsername = async (
    username: string,
    accountId: string,
  ): Promise<UsernameLookupResult | null> => {
    try {
      const result = await client.get<
        UsernameLookupResult | null,
        AxiosResponse<UsernameLookupResult | null>
      >(`${ACCOUNT_USERS_BASE_URL(accountId)}/check-username`, {
        withCredentials: true,
        params: { username },
      });

      return result.status === 204 ? null : result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const downloadImportTemplate = async (accountId: string): Promise<void> => {
    try {
      const result = await client.get(
        `${ACCOUNT_USERS_BASE_URL(accountId)}/import/template`,
        {
          withCredentials: true,
          responseType: 'blob',
        },
      );

      const blob = new Blob([result.data]);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'user-import-template.xlsx';
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(link.href);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const permanentlyRemoveUser = async (
    userId: string,
    accountId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.delete<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${ACCOUNT_USERS_BASE_URL(accountId)}/${userId}/permanent`, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const resendWelcomeEmail = async (
    userId: string,
    accountId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${ACCOUNT_USERS_BASE_URL(
          accountId,
        )}/${userId}/actions/resend-welcome-email`,
        {},
        {
          withCredentials: true,
        },
      );

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const restoreUser = async (
    userId: string,
    accountId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${ACCOUNT_USERS_BASE_URL(accountId)}/${userId}/restore`, null, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const approvePendingUser = async (
    userId: string,
    accountId: string,
    roleId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(
        `${ACCOUNT_USERS_BASE_URL(accountId)}/${userId}/approve`,
        { roleId },
        { withCredentials: true },
      );

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const denyPendingUser = async (
    userId: string,
    accountId: string,
  ): Promise<MessageResponseModel> => {
    try {
      const result = await client.post<
        MessageResponseModel,
        AxiosResponse<MessageResponseModel>
      >(`${ACCOUNT_USERS_BASE_URL(accountId)}/${userId}/deny`, null, {
        withCredentials: true,
      });

      return new MessageResponseModel(result.data);
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const userNotifyUrl = (userId: string, accountId: string) =>
    `${client.defaults.baseURL}accounts/${accountId}/users/notify`;

  return {
    getUsers,
    getAccountUsers,
    getUser,
    saveUser,
    updateUser,
    deleteUser,
    deleteUsers,
    permanentlyRemoveUser,
    restoreUser,
    exportUsers,
    downloadImportTemplate,
    getUserCourses,
    userNotifyUrl,
    getUserCampaigns,
    deleteAnonymizedUsers,
    checkUsername,
    resendWelcomeEmail,
    approvePendingUser,
    denyPendingUser,
  };
};
